import { Link } from 'react-router-dom';

import type { Pun, Reaction } from '../../shared/schemas';
import { countReaction } from '../lib/puns';
import ReactionBar from './ReactionBar';
import ShareButton from './ShareButton';

interface PunCardProps {
  pun: Pun;
  authorName: string;
  viewerId: string;
  crownSpent: boolean;
  onReact: (reaction: Reaction) => void;
  /** Marks the viewer's own entry in a feed of everyone's. */
  highlight?: boolean;
}

/**
 * One pun, wherever it appears. The single copy of this markup: the feed, your
 * own puns and another player's page all render this rather than three
 * near-identical blocks (gotcha 9).
 */
function PunCard({ pun, authorName, viewerId, crownSpent, onReact, highlight }: PunCardProps) {
  const crowns = countReaction(pun, 'crown');

  return (
    <article
      /*
       * A cool tint for your own card, not mustard. The old wash put a yellow
       * field behind yellow emoji and behind the mustard crown badge, so a card
       * of yours was three shades of the same colour.
       */
      className={`rounded-2xl border-2 border-ink px-5 py-5 shadow-pop ${
        highlight ? 'bg-lagoon/10' : 'bg-white'
      }`}
    >
      <div className="mb-3 flex items-baseline justify-between gap-3">
        <Link
          to={`/user/${pun.userId}`}
          className="font-display text-sm font-bold underline decoration-2 underline-offset-2 hover:text-coral"
        >
          {authorName}
        </Link>

        {crowns > 0 && (
          <span className="shrink-0 rounded-full border-2 border-ink bg-mustard px-2 py-0.5 text-xs font-bold tabular-nums">
            {crowns} <span aria-hidden="true">&#128081;</span>
          </span>
        )}
      </div>

      <p className="font-display text-xl font-bold leading-snug">{pun.text}</p>

      <div className="mt-3 flex items-center justify-between gap-3">
        <p className="text-xs text-ink/45">
          {pun.output} &middot; {pun.category}
        </p>

        {/*
          Your own pun shares without a byline. `authorName` is "You" here, and
          "— You" reads as a bug in someone else's group chat.
        */}
        <ShareButton pun={pun} authorName={pun.userId === viewerId ? null : authorName} />
      </div>

      <ReactionBar pun={pun} viewerId={viewerId} crownSpent={crownSpent} onReact={onReact} />
    </article>
  );
}

export default PunCard;
